import { useId } from 'react';

import Button from '@/components/Button/Button';
import Modal from '@/components/Modal/Modal';
import useDashboardWidgets from '@/features/dashboard/hooks/useDashboardWidgets';
import { widgetRegistry, type WidgetId } from '@/features/dashboard/registry';

interface WidgetSettingsModalProps {
  widgetId: WidgetId;
  open: boolean;
  onClose: () => void;
  /** Pass false when opened from inside another modal so back-gesture doesn't pop the outer one. */
  manageHistory?: boolean;
}

const SaveIcon = (
  <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" className="h-4 w-4">
    <path d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V7.414a2 2 0 00-.586-1.414l-2.414-2.414A2 2 0 0013.586 3H4zm2 1.5h6V7a1 1 0 01-1 1H7a1 1 0 01-1-1V4.5zm-.5 7A1.5 1.5 0 017 10h6a1.5 1.5 0 011.5 1.5v4h-9v-4z" />
  </svg>
);

export default function WidgetSettingsModal({ widgetId, open, onClose, manageHistory = true }: WidgetSettingsModalProps) {
  const reg = widgetRegistry[widgetId];
  const { data: resolved } = useDashboardWidgets();
  const entry = resolved?.find((r) => r.id === widgetId);
  const currentConfig = (entry?.doc?.config as Record<string, unknown> | undefined) ?? (reg.defaultConfig as Record<string, unknown>);
  const SettingsForm = reg.SettingsForm;
  const formId = useId();

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`${reg.title} settings`}
      size="sm"
      manageHistory={manageHistory}
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            {SettingsForm ? 'Cancel' : 'Close'}
          </Button>
          {SettingsForm && (
            <Button type="submit" form={formId} variant="primary" leadingIcon={SaveIcon}>
              Save
            </Button>
          )}
        </div>
      }
    >
      {SettingsForm ? (
        <SettingsForm widgetId={widgetId} config={currentConfig} onSaved={onClose} formId={formId} />
      ) : (
        <p className="text-sm text-(--text-tertiary) italic">No settings for this widget.</p>
      )}
    </Modal>
  );
}
